import { isValidElement } from 'react';

import { css } from '@compiled/react';
import { ComponentInstance } from '@uniformdev/canvas';
import { ComponentProps } from '@uniformdev/canvas-react';

import { token } from '@atlaskit/tokens';

import { useTheme } from '@/libraries/design/utils/dark-theme-provider';
import { ProductKeyTypes } from '@/libraries/wpl/foundations/src/data-event-props';
import { LogoBrandV3, LogoV3 } from '@/libraries/wpl/foundations/src/logo-v3';
import {
	DropdownContent,
	DropdownContentItem,
	DropdownContentProps,
} from '@/patterns/global-nav/src/dropdown-content';
import {
	MobileNavLinksContainerInfo,
	MobileNavLinkContentProps,
} from '@/patterns/global-nav/src/mobile-nav/mobile-nav-links-container';
import { AdditionalMenuPageObjects } from '@/patterns/global-nav/src/mobile-nav/product-nav-mobile-menu';
import { GlobalNav } from '@/patterns/global-nav/src/nav-global';
import { CompoundSubNavProps } from '@/patterns/global-nav/src/nav-global-compound-subnav';
import { GlobalNavLinkContentProps } from '@/patterns/global-nav/src/nav-links-container';
import { ProductPageNav } from '@/patterns/global-nav/src/product-page-nav';

import UniformHorizontalDivider from '../../horizontal-divider/src/horizontal-divider';

export interface UniformNavBarProps extends ComponentProps {
	type: 'product' | 'compound';
	primaryLabel: string;
	productKey?: ProductKeyTypes;
	primaryUrl: string;
	ctaText?: string;
	ctaUrl?: string;
	ctaButtonVariant?: 'primary' | 'secondary';
}

const getStringParameter = (component: ComponentInstance, parameterId: string) => {
	const value = component.parameters?.[parameterId]?.value;
	return typeof value === 'string' ? value : undefined;
};

const getSlot = (component: ComponentInstance, slotId: string) => component.slots?.[slotId] ?? [];

const toDropdownLink = (component: ComponentInstance, productKey: ProductKeyTypes) => {
	if (component.type !== 'navLink') {
		return;
	}

	const text = getStringParameter(component, 'linkText');
	const href = getStringParameter(component, 'linkUrl');
	if (!text || !href) {
		return;
	}

	return {
		text,
		href,
		dataEventProps: {
			'data-product-key': productKey,
			'data-event': 'clicked' as const,
			'data-event-component': 'linkButton' as const,
			'data-event-container': 'navbarDropdown' as const,
		},
	};
};

const toDropdownSections = (
	dropdownItems: ComponentInstance[],
	productKey: ProductKeyTypes,
): DropdownContentProps['contentSections'] => {
	const contentItems: DropdownContentItem[] = [];
	let directLinks: NonNullable<ReturnType<typeof toDropdownLink>>[] = [];
	const flushDirectLinks = () => {
		if (directLinks.length > 0) {
			contentItems.push({ links: directLinks });
			directLinks = [];
		}
	};

	dropdownItems.forEach((item) => {
		const directLink = toDropdownLink(item, productKey);
		if (directLink) {
			directLinks.push(directLink);
			return;
		}

		if (item.type === 'navDropdownGroup') {
			flushDirectLinks();
			const header = getStringParameter(item, 'heading');
			const links = getSlot(item, 'navLinks')
				.map((link) => toDropdownLink(link, productKey))
				.filter((link): link is NonNullable<typeof link> => Boolean(link));

			if (links.length > 0) {
				contentItems.push({ header, links });
			}
			return;
		}

		if (item.type === 'horizontalDivider') {
			flushDirectLinks();
			const authoredLineStyle = getStringParameter(item, 'lineStyle');
			const lineStyle = authoredLineStyle === 'dotted' ? 'dotted' : 'solid';
			contentItems.push({
				divider: <UniformHorizontalDivider lineStyle={lineStyle} />,
			});
		}
	});
	flushDirectLinks();

	return contentItems.filter((item, index, items) => {
		const previousItem = items[index - 1];
		return (
			!('divider' in item) ||
			(previousItem !== undefined &&
				!('divider' in previousItem) &&
				items.slice(index + 1).some((laterItem) => !('divider' in laterItem)))
		);
	});
};

const toNavLink = (
	component: ComponentInstance,
	index: number,
	productKey: ProductKeyTypes,
): GlobalNavLinkContentProps | undefined => {
	const globalLinksKey = component._id ?? `${component.type}-${index}`;

	if (component.type === 'navLink') {
		const link = toDropdownLink(component, productKey);
		if (!link) {
			return;
		}

		return {
			globalLinksKey,
			linkText: link.text,
			href: link.href,
			hasDropdown: false,
			productKey,
		};
	}

	if (component.type !== 'navDropdown') {
		return;
	}

	const linkText = getStringParameter(component, 'dropdownText');
	const contentSections = toDropdownSections(getSlot(component, 'navItems'), productKey);
	const hasDivider = contentSections.some((section) => 'divider' in section);
	const activeHrefs = contentSections.flatMap((section) =>
		'divider' in section ? [] : section.links.map((link) => link.href).filter(Boolean),
	);
	if (!linkText || contentSections.length === 0) {
		return;
	}

	return {
		globalLinksKey,
		linkText,
		hasDropdown: true,
		productKey,
		activeHrefs,
		containerPosition: 'relative',
		containerStyles: { width: 'fit-content' },
		content: (
			<DropdownContent
				contentSections={contentSections}
				orientation={hasDivider ? 'column' : undefined}
			/>
		),
	};
};

const toMobileLinks = (links: GlobalNavLinkContentProps[]): MobileNavLinkContentProps[] =>
	links.map((link) => ({
		globalLinksKey: link.globalLinksKey,
		linkText: link.linkText,
		hasDropdown: link.hasDropdown,
		href: link.hasDropdown ? undefined : link.href,
		productKey: link.productKey,
		dropdownType: link.hasDropdown ? link.globalLinksKey : undefined,
	}));

const logoBrandByProductKey: Partial<Record<NonNullable<ProductKeyTypes>, LogoBrandV3>> = {
	atlassianGuard: 'guard',
	jiraServiceManagementAssets: 'assets',
	bamboo: 'bamboo',
	bitbucket: 'bitbucket',
	compass: 'compass',
	confluence: 'confluence',
	crucible: 'crucible',
	customerServiceManagement: 'customerservicemanagement',
	focus: 'focus',
	foundation: 'foundation',
	jira: 'jira',
	jiraAlign: 'jiraalign',
	jiraProductDiscovery: 'jiraproductdiscovery',
	jiraServiceDesk: 'jiraservicemanagement',
	jiraServiceManagement: 'jiraservicemanagement',
	jiraSoftware: 'jira',
	jiraWorkManagement: 'jira',
	loom: 'loom',
	rovo: 'rovo',
	rovoDev: 'rovodev',
	sourcetreeapp: 'sourcetree',
	statuspage: 'statuspage',
	teamworkCollection: 'collectionteamwork',
	talent: 'talent',
	trello: 'trello',
};

const getLogoBrand = (productKey: ProductKeyTypes) =>
	productKey === undefined ? undefined : logoBrandByProductKey[productKey];

const productLabelStyle = css({
	fontSize: token('space.250'),
	fontWeight: token('font.weight.semibold'),
	lineHeight: '29px',
	color: 'var(--wac-color-body)',
});

const UniformNavBar = ({
	component,
	type,
	primaryLabel,
	productKey,
	primaryUrl,
	ctaText,
	ctaUrl,
	ctaButtonVariant = 'primary',
}: UniformNavBarProps) => {
	const analyticsProductKey = productKey ?? 'uniform';
	const navItems = component.slots?.['navItems'] ?? [];
	const navLinks = navItems
		.map((item, index) => toNavLink(item, index, analyticsProductKey))
		.filter((link): link is GlobalNavLinkContentProps => Boolean(link));
	const hasCta = Boolean(ctaText && ctaUrl);
	const { isDarkTheme } = useTheme();

	if (type === 'compound') {
		const compoundNav: CompoundSubNavProps = {
			compoundSubNavTitle: primaryLabel,
			compoundSubNavTitleHref: primaryUrl,
			compoundSubNavLinksProps: navLinks,
			productKey: analyticsProductKey,
			optionalClassName: 'collections-nav',
		};

		return <GlobalNav productKey={analyticsProductKey} compoundNav={compoundNav} />;
	}

	if (type !== 'product') {
		return null;
	}

	const logoBrand = getLogoBrand(productKey);
	const productLogo = logoBrand ? (
		<LogoV3
			brand={logoBrand}
			type="logo"
			appearance="brand"
			height="26px"
			lightMode={isDarkTheme}
		/>
	) : (
		<span css={productLabelStyle}>{primaryLabel}</span>
	);
	const mobileLinks: MobileNavLinksContainerInfo[] = [
		{
			mobileLinkKey: 'uniform-nav-links',
			mobileLinksProps: toMobileLinks(navLinks),
		},
	];
	const additionalMenuPageObjectsArray: AdditionalMenuPageObjects[] = navLinks.flatMap((link) =>
		link.hasDropdown && isValidElement(link.content)
			? [{ menuPageKey: link.globalLinksKey, menuPageObjects: link.content }]
			: [],
	);

	return (
		<ProductPageNav
			productLogo={productLogo}
			mobileLogo={productLogo}
			productLogoUrl={primaryUrl}
			productLogoAriaLabel={primaryLabel}
			productKey={analyticsProductKey}
			productPageNavLinks={navLinks}
			mobileLinksSections={mobileLinks}
			additionalMenuPageObjectsArray={additionalMenuPageObjectsArray}
			isShowTryNowLink={hasCta}
			isIncludeMobileCTA={hasCta}
			navigationTrynowCtaText={hasCta ? ctaText : undefined}
			navigationTrynowCtaUrl={hasCta ? ctaUrl : undefined}
			navigationTrynowCtaButtonVariant={ctaButtonVariant}
			mobileCtaText={hasCta ? ctaText : undefined}
			mobileCtaUrl={hasCta ? ctaUrl : undefined}
			mobileCtaButtonVariant={ctaButtonVariant}
		/>
	);
};

export default UniformNavBar;
